import React, { useEffect, useState } from "react";
import "./CreateClientDialog.css";

const TITLES             = ["Mr.", "Ms.", "Mrs.", "Dr.", "Other"];
const SOURCES            = ["Website", "Referral", "Social Media", "Agent", "Walk-in", "Lead", "Other"];
const STATUSES           = ["New", "Active", "Converted", "Inactive"];
const PASSPORT_CATEGORIES = ["ECR", "NON-ECR", "Other"];
const VISA_TYPES         = ["Tourist", "Business", "Student", "Work", "Other"];
const ENTRY_CATEGORIES   = ["Single", "Multiple"];

const emptyForm = {
  title: "Mr.",
  firstName: "", lastName: "",
  dateOfBirth: "", phone: "", email: "", nationality: "",
  source: "Website", status: "New", bookingType: "direct_client", notes: "",
  passportNumber: "", passportCategory: "NON-ECR",
  passportIssueDate: "", passportExpiryDate: "", passportIssuingCountry: "",
  activeVisaCountry: "", visaNumber: "", visaType: "Tourist",
  entryCategory: "Single", visaIssueDate: "", visaExpiryDate: "",
};

/* ── Helpers ────────────────────────────────────────────────────────────── */
function passportValidity(expiryDate) {
  if (!expiryDate) return null;
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const ms     = expiry - today;
  if (ms <= 0) return { expired: true, label: "Passport expired" };
  const totalDays = Math.floor(ms / (1000 * 60 * 60 * 24));
  const months    = Math.floor(totalDays / 30);
  const days      = totalDays % 30;
  return { expired: false, label: `${months}m ${days}d remaining` };
}

function visaDays(issueDate, expiryDate) {
  if (!issueDate || !expiryDate) return null;
  const days = Math.round((new Date(expiryDate) - new Date(issueDate)) / 86400000);
  return days > 0 ? days : 0;
}

/* ── File upload sub-component ──────────────────────────────────────────── */
function FileUpload({ label, name, onChange, existingUrl, accept = "image/jpeg,image/png,application/pdf" }) {
  const [fileName, setFileName] = useState("");

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) { setFileName(file.name); onChange(name, file); }
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="file-upload-area">
        <input type="file" accept={accept} onChange={handleChange} />
        <div className="file-upload-label">
          {fileName
            ? <span className="file-preview">📎 {fileName}</span>
            : <><strong>Click to upload</strong> or drag &amp; drop<br /><span style={{ fontSize: 11 }}>JPEG, PNG, PDF — max 5 MB</span></>
          }
        </div>
      </div>
      {existingUrl && !fileName && (
        <div className="existing-file">Current: <a href={existingUrl} target="_blank" rel="noreferrer">View file</a></div>
      )}
    </div>
  );
}

/* ── Main dialog ────────────────────────────────────────────────────────── */
export default function CreateClientDialog({ open, onClose, onSubmit, initialData, isLoading }) {
  const [form, setForm]       = useState(emptyForm);
  const [hasVisa, setHasVisa] = useState(false);
  const [files, setFiles]     = useState({});

  useEffect(() => {
    if (initialData) {
      const { _id, __v, createdAt, updatedAt, assignedAgent, visas, ...rest } = initialData;
      const safeData = { ...emptyForm, ...rest };
      ["dateOfBirth", "passportIssueDate", "passportExpiryDate"].forEach((f) => {
        if (safeData[f]) safeData[f] = new Date(safeData[f]).toISOString().split("T")[0];
      });
      setForm(safeData);
      setFiles({});
      if (visas?.length > 0) {
        const v = visas[0];
        setHasVisa(true);
        setForm((prev) => ({
          ...prev,
          activeVisaCountry: v.activeVisaCountry || "",
          visaNumber:        v.visaNumber || "",
          visaType:          v.visaType || "Tourist",
          entryCategory:     v.entryCategory || "Single",
          visaIssueDate:     v.visaIssueDate ? new Date(v.visaIssueDate).toISOString().split("T")[0] : "",
          visaExpiryDate:    v.visaExpiryDate ? new Date(v.visaExpiryDate).toISOString().split("T")[0] : "",
        }));
      } else {
        setHasVisa(false);
      }
    } else {
      setForm(emptyForm);
      setFiles({});
      setHasVisa(false);
    }
  }, [initialData, open]);

  if (!open) return null;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const handleChange = (e) => set(e.target.name, e.target.value);
  const handleFileChange = (name, file) => setFiles((f) => ({ ...f, [name]: file }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    [
      "title","firstName","lastName","dateOfBirth","phone","email","nationality",
      "source","status","bookingType","notes",
      "passportNumber","passportCategory","passportIssueDate","passportExpiryDate","passportIssuingCountry",
    ].forEach((k) => { if (form[k] !== undefined && form[k] !== "") fd.append(k, form[k]); });

    if (files.passportFrontImage) fd.append("passportFrontImage", files.passportFrontImage);
    if (files.passportBackImage)  fd.append("passportBackImage",  files.passportBackImage);
    if (files.visaDocument)        fd.append("visaDocument",       files.visaDocument);

    if (hasVisa) {
      fd.append("visa", JSON.stringify({
        activeVisaCountry: form.activeVisaCountry,
        visaNumber:        form.visaNumber,
        visaType:          form.visaType,
        entryCategory:     form.entryCategory,
        visaIssueDate:     form.visaIssueDate || undefined,
        visaExpiryDate:    form.visaExpiryDate || undefined,
      }));
    }
    onSubmit(fd);
  };

  const pv   = passportValidity(form.passportExpiryDate);
  const vd   = visaDays(form.visaIssueDate, form.visaExpiryDate);

  return (
    <div className="modal-backdrop">
      {/* ── Shell: fixed height, flex column ── */}
      <div className="modal">

        {/* ── Sticky header — never scrolls ── */}
        <div className="modal-header">
          <h2>{initialData ? "Edit Client" : "Add Client"}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="modal-form">

            {/* Personal Information */}
            <div className="section-title">Personal Information</div>

            <div className="title-name-row" style={{ marginBottom: 14 }}>
              <div className="form-group">
                <label>Title</label>
                <select name="title" value={form.title} onChange={handleChange}>
                  {TITLES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Given Name (as per passport) *</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} required placeholder="First Name" />
              </div>
              <div className="form-group">
                <label>Last Name (as per passport) *</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Last Name" />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Contact Number</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
              </div>
              <div className="form-group">
                <label>Email ID *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="client@example.com" />
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <input name="nationality" value={form.nationality} onChange={handleChange} placeholder="e.g. Indian, British" />
              </div>
              <div className="form-group">
                <label>Source</label>
                <select name="source" value={form.source} onChange={handleChange}>
                  {SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleChange}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Booking Type *</label>
                <select name="bookingType" value={form.bookingType} onChange={handleChange} required>
                  <option value="direct_client">Direct Client</option>
                  <option value="agent">Agent</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea name="notes" rows="2" value={form.notes} onChange={handleChange} placeholder="Any additional notes..." />
              </div>
            </div>

            {/* Passport Details */}
            <div className="section-title">Passport Details</div>

            <div className="form-grid">
              <div className="form-group">
                <label>Passport Number</label>
                <input name="passportNumber" value={form.passportNumber} onChange={handleChange} placeholder="A1234567" />
              </div>
              <div className="form-group">
                <label>Passport Category</label>
                <select name="passportCategory" value={form.passportCategory} onChange={handleChange}>
                  {PASSPORT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Issue Date</label>
                <input type="date" name="passportIssueDate" value={form.passportIssueDate} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Expiry Date</label>
                <input type="date" name="passportExpiryDate" value={form.passportExpiryDate} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Issuing Country</label>
                <input name="passportIssuingCountry" value={form.passportIssuingCountry} onChange={handleChange} placeholder="e.g. India, USA" />
              </div>
              <div className="form-group" style={{ justifyContent: "flex-end" }}>
                <label>Validity Remaining (auto)</label>
                {pv ? (
                  <div className={`auto-badge${pv.expired ? " expired" : ""}`}>
                    {pv.expired ? "⚠️" : "✅"} {pv.label}
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: "#9ca3af", marginTop: 6 }}>Set expiry date to calculate</span>
                )}
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <FileUpload label="Passport Front Page (scan)" name="passportFrontImage" onChange={handleFileChange} existingUrl={initialData?.passportFrontImage} />
              <FileUpload label="Passport Back Page (scan)"  name="passportBackImage"  onChange={handleFileChange} existingUrl={initialData?.passportBackImage} />
            </div>

            {/* Visa Details */}
            <div className="section-title">Visa Details</div>

            <div className="visa-toggle">
              <input type="checkbox" id="hasVisa" checked={hasVisa} onChange={(e) => setHasVisa(e.target.checked)} />
              <label htmlFor="hasVisa">Client has an active visa to add</label>
            </div>

            {hasVisa && (
              <>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Active Visa Country</label>
                    <input name="activeVisaCountry" value={form.activeVisaCountry} onChange={handleChange} placeholder="e.g. UAE, USA, UK" />
                  </div>
                  <div className="form-group">
                    <label>Visa Number</label>
                    <input name="visaNumber" value={form.visaNumber} onChange={handleChange} placeholder="Visa reference number" />
                  </div>
                  <div className="form-group">
                    <label>Visa Type</label>
                    <select name="visaType" value={form.visaType} onChange={handleChange}>
                      {VISA_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Entry Category</label>
                    <select name="entryCategory" value={form.entryCategory} onChange={handleChange}>
                      {ENTRY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Visa Issue Date</label>
                    <input type="date" name="visaIssueDate" value={form.visaIssueDate} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Visa Expiry Date</label>
                    <input type="date" name="visaExpiryDate" value={form.visaExpiryDate} onChange={handleChange} min={form.visaIssueDate} />
                  </div>
                  <div className="form-group" style={{ justifyContent: "flex-end" }}>
                    <label>Visa Validity (auto)</label>
                    {vd !== null ? (
                      <div className={`auto-badge${vd === 0 ? " expired" : ""}`}>
                        {vd === 0 ? "⚠️ 0 days" : `✅ ${vd} day${vd !== 1 ? "s" : ""}`}
                      </div>
                    ) : (
                      <span style={{ fontSize: 13, color: "#9ca3af", marginTop: 6 }}>Set both dates to calculate</span>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <FileUpload label="Visa Copy (scan)" name="visaDocument" onChange={handleFileChange} existingUrl={initialData?.visas?.[0]?.visaDocumentFront} />
                </div>
              </>
            )}

            {/* Actions */}
            <div className="modal-actions">
              <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? "Saving..." : initialData ? "Update Client" : "Create Client"}
              </button>
            </div>

          </form>
        </div>
        {/* end .modal-body */}

      </div>
      {/* end .modal */}
    </div>
  );
}